import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import { api, useSEO } from "../../lib/api";
import { useShop } from "../../context/ShopContext";
import ProductCard from "../../components/store/ProductCard";

const SORTS = [["popularity", "Popularity"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["newest", "Newest"], ["rating", "Best Rated"]];
const PRICES = [["", "Any price"], ["0-299", "Under ₹300"], ["300-499", "₹300 – ₹499"], ["500-999", "₹500 – ₹999"], ["1000-0", "₹1000 & above"]];

function Filters({ sp, set, facets, categories, catSlug }) {
  const chip = (active) => `text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#1B3B2B] text-white border-[#1B3B2B]" : "border-[#E8E0D5] hover:bg-[#E2EBE2]"}`;
  const Group = ({ title, k, values }) => values?.length ? (
    <div><p className="eyebrow mb-3">{title}</p><div className="flex flex-wrap gap-2">
      {values.map((v) => <button key={v} className={chip(sp.get(k) === v)} onClick={() => set(k, sp.get(k) === v ? "" : v)} data-testid={`filter-${k}-${v.toLowerCase().replace(/\s+/g, "-")}`}>{v}</button>)}
    </div></div>
  ) : null;
  return (
    <div className="space-y-7">
      <div><p className="eyebrow mb-3">Category</p><div className="flex flex-wrap gap-2">
        <button className={chip(!catSlug && !sp.get("category"))} onClick={() => set("category", "")} data-testid="filter-category-all">All</button>
        {categories.map((c) => <button key={c.id} className={chip((catSlug || sp.get("category")) === c.slug)} onClick={() => set("category", c.slug)} data-testid={`filter-category-${c.slug}`}>{c.name}</button>)}
      </div></div>
      <div><p className="eyebrow mb-3">Price</p><div className="flex flex-wrap gap-2">
        {PRICES.map(([v, l]) => <button key={v} className={chip((sp.get("price") || "") === v)} onClick={() => set("price", v)} data-testid={`filter-price-${v || "any"}`}>{l}</button>)}
      </div></div>
      <Group title="Skin type" k="skin" values={facets.skin_types} />
      <Group title="Hair type" k="hair" values={facets.hair_types} />
      <Group title="Concern" k="concern" values={facets.concerns} />
      <div className="space-y-2 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={sp.get("in_stock") === "1"} onChange={(e) => set("in_stock", e.target.checked ? "1" : "")} data-testid="filter-in-stock" /> In stock only</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={sp.get("offers") === "1"} onChange={(e) => set("offers", e.target.checked ? "1" : "")} data-testid="filter-offers" /> On offer</label>
      </div>
    </div>
  );
}

export default function Shop() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { categories } = useShop();
  const [res, setRes] = useState({ items: [], total: 0 });
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const catSlug = slug || sp.get("category") || "";
  const cat = categories.find((c) => c.slug === catSlug);
  const tag = sp.get("tag");
  const title = sp.get("q") ? `Results for “${sp.get("q")}”` : cat?.name || (tag === "new_arrival" ? "New Arrivals" : tag === "bestseller" ? "Best Sellers" : sp.get("offers") ? "Offers" : "Shop All");
  useSEO({ title: cat?.seo_title || title, description: cat?.seo_description || cat?.description });

  useEffect(() => {
    api.get("/products/facets").then((r) => setFacets(r.data));
  }, []);
  useEffect(() => {
    const [min, max] = (sp.get("price") || "").split("-");
    const params = { q: sp.get("q") || "", category: catSlug, skin: sp.get("skin") || "", hair: sp.get("hair") || "", concern: sp.get("concern") || "",
      min_price: min || 0, max_price: max || 0, in_stock: sp.get("in_stock") === "1", offers: sp.get("offers") === "1", tag: tag || "", sort: sp.get("sort") || "popularity", limit: 48 };
    setLoading(true);
    api.get("/products", { params }).then((r) => setRes(r.data)).finally(() => setLoading(false));
  }, [sp, catSlug, tag]);
  const set = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  return (
    <div className="container-x py-10 sm:py-14">
      <p className="eyebrow mb-2">SEED COSMETICS</p>
      <h1 className="text-4xl sm:text-5xl text-[#1B3B2B] tracking-tight" data-testid="shop-title">{title}</h1>
      {cat?.description && <p className="text-stone-600 mt-3 max-w-2xl">{cat.description}</p>}
      <div className="flex items-center justify-between mt-8 mb-6 gap-3">
        <p className="text-sm text-stone-500" data-testid="shop-result-count">{res.total} products</p>
        <div className="flex gap-2">
          <button className="lg:hidden flex items-center gap-1.5 text-sm border border-[#E8E0D5] rounded-full px-4 py-2" onClick={() => setOpen(true)} data-testid="mobile-filter-button"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
          <select value={sp.get("sort") || "popularity"} onChange={(e) => set("sort", e.target.value)} data-testid="sort-select" className="text-sm border border-[#E8E0D5] rounded-full px-4 py-2 bg-[#FFFDF9] outline-none">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block"><Filters sp={sp} set={set} facets={facets} categories={categories} catSlug={catSlug} /></aside>
        <div>
          {loading ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-[#F4EFE6] animate-pulse" />)}</div>
            : res.items.length ? <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6" data-testid="product-grid">{res.items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
              : <p className="text-stone-500 bg-[#F4EFE6] rounded-xl p-6" data-testid="no-results">No products match these filters yet.</p>}
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-[#FBF8F3] overflow-y-auto"><SheetTitle className="font-serif text-2xl mb-6">Filters</SheetTitle><Filters sp={sp} set={set} facets={facets} categories={categories} catSlug={catSlug} /></SheetContent>
      </Sheet>
    </div>
  );
}
